import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import apiService from '../services/api';
import { AUTH_TESTIMONIALS, AUTH_SUBTITLE, authBgUrl, AUTH_BG_UNSPLASH_IDS } from '../config/authTestimonials';
import {
  BrandMark,
  DottedRule,
  HighlightedWord,
  INDIGO,
  INDIGO_DARK,
  INK,
  INK_SOFT,
  INTER_STACK,
  LeftPanel,
  SkuKicker,
} from '../components/auth/TicketTape';

const MIN_PASSWORD_LENGTH = 6;

/**
 * /reset-password?token=… — choose a new password (DashFlow "ticket tape" theme).
 */
export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const reducedMotion = useReducedMotion() ?? false;
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const fieldSx = {
    fontFamily: INTER_STACK,
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      fontFamily: INTER_STACK,
      bgcolor: '#fff',
      '& fieldset': { borderColor: 'rgba(15,23,42,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.32)' },
      '&.Mui-focused fieldset': { borderColor: INDIGO, borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontFamily: INTER_STACK, color: INK_SOFT },
    '& .MuiInputLabel-root.Mui-focused': { color: INDIGO },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await apiService.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'No pudimos restablecer la contraseña. El enlace puede haber expirado.');
    } finally {
      setLoading(false);
    }
  };

  const bgUrl = authBgUrl(AUTH_BG_UNSPLASH_IDS[0]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', fontFamily: INTER_STACK, bgcolor: '#ffffff' }}>
      {isDesktop && (
        <LeftPanel
          kicker="LOTE · RESET-042"
          headline={
            <>
              Una contraseña
              <br />
              <HighlightedWord>nueva</HighlightedWord>, y listo.
            </>
          }
          subtitle={AUTH_SUBTITLE}
          testimonials={AUTH_TESTIMONIALS}
          bgUrl={bgUrl}
          reducedMotion={reducedMotion}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          width: { xs: '100%', md: '50%' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 5, md: 7, lg: 10 },
          py: { xs: 5, md: 6 },
          bgcolor: '#ffffff',
        }}
      >
        {!isDesktop && (
          <Box sx={{ alignSelf: 'flex-start', mb: 4 }}>
            <BrandMark color={INK} size="md" />
          </Box>
        )}

        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ mb: 2 }}>
            <SkuKicker label="SKU · RESET-042" />
          </Box>

          <Typography
            component="h2"
            sx={{ fontFamily: INTER_STACK, fontSize: { xs: 26, sm: 30 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, color: INK, mb: 1 }}
          >
            Nueva <HighlightedWord>contraseña</HighlightedWord>.
          </Typography>
          <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK_SOFT, mb: 3 }}>
            {done ? 'Listo. Te llevamos al inicio de sesión…' : 'Elegí una contraseña nueva para tu cuenta.'}
          </Typography>

          <DottedRule my={0} />

          {error && (
            <Alert severity="error" sx={{ mt: 2.5, fontFamily: INTER_STACK, border: '1px solid rgba(220,38,38,0.25)', borderRadius: '5px' }}>
              {error}
            </Alert>
          )}

          {!token ? (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ border: '1px solid rgba(220,38,38,0.4)', borderRadius: '6px', bgcolor: 'rgba(220,38,38,0.05)', p: 2.5, mb: 3 }}>
                <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK, lineHeight: 1.55 }}>
                  Este enlace no es válido o está incompleto. Pedí uno nuevo.
                </Typography>
              </Box>
              <Link component={RouterLink} to="/forgot-password" sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}`, pb: '1px', '&:hover': { borderBottomStyle: 'solid' } }}>
                Pedir un nuevo enlace →
              </Link>
            </Box>
          ) : done ? (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ border: `1px solid ${INDIGO}`, borderRadius: '6px', bgcolor: 'rgba(56,77,244,0.05)', p: 2.5, mb: 3 }}>
                <Typography sx={{ fontFamily: INTER_STACK, fontSize: 14.5, color: INK, lineHeight: 1.55 }}>
                  Contraseña actualizada. Redirigiendo al inicio de sesión…
                </Typography>
              </Box>
              <Link component={RouterLink} to="/login" sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}`, pb: '1px', '&:hover': { borderBottomStyle: 'solid' } }}>
                Ir al inicio de sesión →
              </Link>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2.5 }}>
              <TextField
                fullWidth
                label="Nueva contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                required
                autoComplete="new-password"
                autoFocus
                helperText={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                sx={{ mb: 2, ...fieldSx }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" aria-label="Mostrar contraseña" size="small">
                        {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Repetir contraseña"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setError('');
                }}
                required
                autoComplete="new-password"
                sx={{ mb: 3, ...fieldSx }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !password || !confirm}
                disableElevation
                sx={{
                  py: 1.4,
                  borderRadius: '5px',
                  fontFamily: INTER_STACK,
                  fontWeight: 600,
                  fontSize: 15,
                  textTransform: 'none',
                  bgcolor: INDIGO,
                  color: '#fff',
                  '&:hover': { bgcolor: INDIGO_DARK },
                }}
              >
                {loading ? 'Guardando…' : 'Guardar contraseña'}
              </Button>

              <Typography sx={{ mt: 3, textAlign: 'center', fontFamily: INTER_STACK, fontSize: 14, color: INK_SOFT }}>
                <Link component={RouterLink} to="/login" sx={{ fontFamily: INTER_STACK, color: INDIGO, fontWeight: 600, textDecoration: 'none', borderBottom: `1px dotted ${INDIGO}`, pb: '1px', '&:hover': { borderBottomStyle: 'solid' } }}>
                  ← Volver al inicio de sesión
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
